import { createMemo, createSignal, Show, type JSX } from "solid-js"
import { produce } from "solid-js/store"
import { useMutation } from "@tanstack/solid-query"
import { Popover as KobaltePopover } from "@kobalte/core/popover"
import { Binary } from "@opencode-ai/core/util/binary"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { DialogFooter, DialogHeader, DialogTitleGroup, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { notifySessionTabsRemoved } from "@/components/titlebar-session-events"
import { useLanguage } from "@/context/language"
import { usePlatform } from "@/context/platform"
import { useServerSDK } from "@/context/server-sdk"
import { useServerSync } from "@/context/server-sync"
import { errorMessage } from "@/pages/layout/helpers"
import { downloadSessionExport, fetchSessionExport, sessionExportFilename } from "@/utils/session-export"
import { sessionTitle } from "@/utils/session-title"
import { showToast } from "@/utils/toast"

// 会话行右侧「...」下拉菜单：重命名 / 分享… / 导出… / 归档 / 删除…
// 与会话页标题栏（message-timeline）的同名菜单保持一致，供左侧栏等列表场景复用。
// 重命名不在这里接管标题 DOM：菜单收起后回调 onRename，由调用方把标题切成行内编辑。
export function SessionActionsMenu(props: {
  sessionID: string
  directory: string
  onRename: () => void
  onOpenChange?: (open: boolean) => void
}): JSX.Element {
  const language = useLanguage()
  const platform = usePlatform()
  const dialog = useDialog()
  const serverSDK = useServerSDK()
  const serverSync = useServerSync()

  const [store, setStore] = serverSync().child(props.directory)
  const session = createMemo(() => store.session.find((item) => item.id === props.sessionID))
  const shareUrl = createMemo(() => session()?.share?.url)
  const shareEnabled = createMemo(() => store.config?.share !== "disabled")

  const [menuOpen, setMenuOpen] = createSignal(false)
  // Kobalte 在菜单关闭时会把焦点还给触发按钮，会打断紧接着打开的行内编辑 / 分享浮层，
  // 所以先记一笔"关闭原因"，在 onCloseAutoFocus 里阻止默认行为后再接手。
  const [pendingRename, setPendingRename] = createSignal(false)
  const [pendingShare, setPendingShare] = createSignal(false)
  const [share, setShare] = createSignal<{ open: boolean; dismiss: "escape" | "outside" | null }>({
    open: false,
    dismiss: null,
  })
  let trigger: HTMLButtonElement | undefined

  const openMenu = (open: boolean) => {
    setMenuOpen(open)
    props.onOpenChange?.(open)
  }

  // 会话按目录归属，所有写操作都带上 directory，避免落到服务端默认目录。
  const client = () => serverSDK().createClient({ directory: props.directory, throwOnError: true })

  const removeFromList = () => {
    setStore(
      produce((draft) => {
        const match = Binary.search(draft.session, props.sessionID, (item) => item.id)
        if (match.found) draft.session.splice(match.index, 1)
      }),
    )
    notifySessionTabsRemoved({ directory: props.directory, sessionIDs: [props.sessionID] })
  }

  const shareMutation = useMutation(() => ({
    mutationFn: (id: string) => client().session.share({ sessionID: id }),
    onError: (err) => console.error("Failed to share session", err),
  }))

  const unshareMutation = useMutation(() => ({
    mutationFn: (id: string) => client().session.unshare({ sessionID: id }),
    onError: (err) => console.error("Failed to unshare session", err),
  }))

  const shareSession = () => {
    if (shareMutation.isPending || !shareEnabled()) return
    shareMutation.mutate(props.sessionID)
  }

  const unshareSession = () => {
    if (unshareMutation.isPending || !shareEnabled()) return
    unshareMutation.mutate(props.sessionID)
  }

  const viewShare = () => {
    const url = shareUrl()
    if (url) void platform.openExternal(url)
  }

  const copyShareUrl = () => {
    const url = shareUrl()
    if (!url) return
    void navigator.clipboard
      .writeText(url)
      .then(() =>
        showToast({
          variant: "success",
          icon: "circle-check",
          title: language.t("session.share.copy.copied"),
          description: url,
        }),
      )
      .catch((err: unknown) =>
        showToast({
          title: language.t("common.requestFailed"),
          description: errorMessage(err, language.t("common.requestFailed")),
        }),
      )
  }

  const exportSession = async () => {
    try {
      const data = await fetchSessionExport({ sessionID: props.sessionID, client: client() })
      const filename = sessionExportFilename(data.info)
      downloadSessionExport(filename, data)
      showToast({
        variant: "success",
        icon: "circle-check",
        title: language.t("toast.session.export.success.title"),
        description: language.t("toast.session.export.success.description", { filename }),
      })
    } catch (err) {
      showToast({
        variant: "error",
        title: language.t("toast.session.export.failed.title"),
        description: err instanceof Error ? err.message : language.t("toast.session.export.failed.description"),
      })
    }
  }

  // 归档只对 v1 协议可用（与会话页 / 首页列表保持一致）
  const archiveSession = async () => {
    if ((await serverSDK().protocol) !== "v1") return
    await client()
      .session.update({ sessionID: props.sessionID, directory: props.directory, time: { archived: Date.now() } })
      .then(removeFromList)
      .catch((err) =>
        showToast({
          title: language.t("common.requestFailed"),
          description: errorMessage(err, language.t("common.requestFailed")),
        }),
      )
  }

  const deleteSession = async () => {
    await serverSDK()
      .api.session.remove({ sessionID: props.sessionID, directory: props.directory })
      .then(removeFromList)
      .catch((err) =>
        showToast({
          title: language.t("session.delete.failed.title"),
          description: errorMessage(err, language.t("session.delete.failed.title")),
        }),
      )
  }

  const confirmDelete = () => {
    const name = sessionTitle(session()?.title) ?? language.t("command.session.new")
    dialog.show(() => (
      <DialogDeleteSession name={name} onConfirm={deleteSession} />
    ))
  }

  return (
    <>
      <MenuV2 gutter={6} placement="bottom-end" open={menuOpen()} onOpenChange={openMenu}>
        <MenuV2.Trigger
          as={IconButtonV2}
          ref={(el: HTMLButtonElement) => {
            trigger = el
          }}
          variant="ghost-muted"
          size="small"
          icon={<IconV2 name="outline-dots" />}
          aria-label={language.t("common.moreOptions")}
          aria-expanded={menuOpen() || share().open || pendingShare()}
          data-action="session-menu"
          data-session={props.sessionID}
        />
        <MenuV2.Portal>
          <MenuV2.Content
            style={{ width: "120px", "min-width": "120px" }}
            onCloseAutoFocus={(event) => {
              if (pendingRename()) {
                event.preventDefault()
                setPendingRename(false)
                // 不能在关闭回调里同步切编辑态：调用方会就地卸载这个菜单组件，
                // 而此刻下拉菜单自己的关闭流程还没跑完。延后一帧再交接。
                requestAnimationFrame(() => props.onRename())
                return
              }
              if (pendingShare()) {
                event.preventDefault()
                requestAnimationFrame(() => {
                  setShare({ open: true, dismiss: null })
                  setPendingShare(false)
                })
              }
            }}
          >
            <MenuV2.Item
              onSelect={() => {
                setPendingRename(true)
                openMenu(false)
              }}
            >
              {language.t("common.rename")}
            </MenuV2.Item>
            <Show when={shareEnabled()}>
              <MenuV2.Item
                onSelect={() => {
                  setPendingShare(true)
                  openMenu(false)
                }}
              >
                {language.t("session.share.action.share")}...
              </MenuV2.Item>
            </Show>
            <MenuV2.Item onSelect={() => void exportSession()}>{language.t("common.export")}...</MenuV2.Item>
            <MenuV2.Item onSelect={() => void archiveSession()}>{language.t("common.archive")}</MenuV2.Item>
            <MenuV2.Separator />
            <MenuV2.Item onSelect={confirmDelete}>{language.t("common.delete")}...</MenuV2.Item>
          </MenuV2.Content>
        </MenuV2.Portal>
      </MenuV2>

      <KobaltePopover
        open={share().open}
        anchorRef={() => trigger}
        placement="bottom-end"
        gutter={6}
        modal={false}
        onOpenChange={(open) => {
          if (open) setShare({ open, dismiss: null })
          else setShare((prev) => ({ open, dismiss: prev.dismiss }))
        }}
      >
        <KobaltePopover.Portal>
          <KobaltePopover.Content
            data-component="popover-content"
            class="flex w-80 max-w-none flex-col items-start gap-3 rounded-[10px] border-0 bg-v2-background-bg-layer-01 p-3 shadow-[var(--v2-elevation-floating)]"
            style={{ "min-width": "320px" }}
            onEscapeKeyDown={(event) => {
              setShare({ dismiss: "escape", open: false })
              event.preventDefault()
              event.stopPropagation()
            }}
            onPointerDownOutside={() => setShare({ dismiss: "outside", open: false })}
            onFocusOutside={() => setShare({ dismiss: "outside", open: false })}
            onCloseAutoFocus={(event) => {
              if (share().dismiss === "outside") event.preventDefault()
              setShare((prev) => ({ ...prev, dismiss: null }))
            }}
          >
            <div class="flex w-full flex-col gap-1.5 px-0.5 pt-0.5">
              <div class="select-none text-[13px] font-[530] leading-none tracking-[-0.04px] text-v2-text-text-base [font-variation-settings:'slnt'_0]">
                {language.t("session.share.popover.title")}
              </div>
              <div class="select-none text-[13px] font-[440] leading-5 tracking-[-0.04px] text-v2-text-text-muted [font-variation-settings:'slnt'_0]">
                {shareUrl()
                  ? language.t("session.share.popover.description.shared")
                  : language.t("session.share.popover.description.unshared")}
              </div>
            </div>
            <div class="flex w-full flex-col gap-2">
              <Show
                when={shareUrl()}
                fallback={
                  <ButtonV2
                    variant="contrast"
                    class="w-full"
                    onClick={shareSession}
                    disabled={shareMutation.isPending}
                  >
                    {shareMutation.isPending
                      ? language.t("session.share.action.publishing")
                      : language.t("session.share.action.publish")}
                  </ButtonV2>
                }
              >
                <div class="flex flex-col gap-2">
                  <div
                    class="flex h-8 w-full items-center gap-1.5 rounded-[6px] py-1 pl-2.5 pr-1.5 shadow-[var(--v2-elevation-button-neutral)]"
                    style={{
                      background:
                        "linear-gradient(180deg, var(--v2-alpha-light-2) 0%, var(--v2-alpha-light-0) 100%), var(--v2-background-bg-button-neutral)",
                    }}
                  >
                    <div class="min-w-0 flex-1 truncate select-text cursor-text text-[13px] font-[440] leading-5 tracking-[-0.04px] text-v2-text-text-base [font-variation-settings:'slnt'_0]">
                      {shareUrl()}
                    </div>
                    <IconButtonV2
                      type="button"
                      size="small"
                      variant="ghost-muted"
                      icon={<IconV2 name="outline-copy" />}
                      aria-label={language.t("session.share.copy.copyLink")}
                      onClick={copyShareUrl}
                    />
                    <IconButtonV2
                      type="button"
                      size="small"
                      variant="ghost-muted"
                      icon={<IconV2 name="outline-square-arrow" />}
                      aria-label={language.t("session.share.action.view")}
                      onClick={viewShare}
                      disabled={unshareMutation.isPending}
                    />
                  </div>
                  <div class="flex w-full">
                    <ButtonV2
                      variant="outline"
                      class="w-full"
                      onClick={unshareSession}
                      disabled={unshareMutation.isPending}
                    >
                      {unshareMutation.isPending
                        ? language.t("session.share.action.unpublishing")
                        : language.t("session.share.action.unpublish")}
                    </ButtonV2>
                  </div>
                </div>
              </Show>
            </div>
          </KobaltePopover.Content>
        </KobaltePopover.Portal>
      </KobaltePopover>
    </>
  )
}

function DialogDeleteSession(props: { name: string; onConfirm: () => Promise<void> }): JSX.Element {
  const language = useLanguage()
  const dialog = useDialog()

  return (
    <DialogV2 fit>
      <DialogHeader hideClose>
        <DialogTitleGroup
          title={language.t("session.delete.title")}
          description={language.t("session.delete.confirm", { name: props.name })}
        />
      </DialogHeader>
      <DialogFooter>
        <ButtonV2 variant="ghost" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2
          variant="danger"
          onClick={async () => {
            await props.onConfirm()
            dialog.close()
          }}
        >
          {language.t("session.delete.button")}
        </ButtonV2>
      </DialogFooter>
    </DialogV2>
  )
}
